/**
 * The editor card — identity fields, rule builder with live match count,
 * matches preview, save/cancel/delete.
 * For Media Devoted | Created By Ciro Autiero
 */
import { FC } from 'react';
import { Link } from 'react-router-dom';
import { Rows3, Settings } from 'lucide-react';

import { Button, Card, ConfirmDialog } from '@/components/ui';
import { useStaticTags } from '@/hooks/smswiser/static-tags/useStaticTags';

import type { ListGroup } from '../../list-groups.types';
import { useDraftRuleResolve } from '../../useListGroups';
import { RuleBuilder } from '../rule-builder';
import { EditorFields } from './EditorFields';
import { MatchesRightNow } from './matches';
import { useListGroupEditorState } from './useListGroupEditorState';

interface Props {
  group: ListGroup | null;
  isNew: boolean;
  countryKey: string;
  countryName: string;
  /** Tracker stats warming — the live resolve would hang for minutes, so
   *  it stays off and the matches panel says why. */
  statsWarming?: boolean;
  onDone: (created?: ListGroup) => void;
}

export const ListGroupEditorCard: FC<Props> = ({
  group,
  isNew,
  countryKey,
  countryName,
  statsWarming = false,
  onDone,
}) => {
  const state = useListGroupEditorState({ group, isNew, countryKey, onDone });
  const staticTags = useStaticTags();
  const resolve = useDraftRuleResolve(
    countryKey,
    state.draft.rule.nodes.length > 0 ? state.draft.rule : null,
    !statsWarming,
  );

  return (
    <Card className="flex flex-col gap-6 p-6">
      <div className="flex items-center justify-between">
        <h2 className="flex items-center gap-2 text-sm font-semibold">
          <Settings className="h-4 w-4 text-muted-foreground" />
          {isNew ? 'New group' : 'Editing'}
          {state.draft.name.trim() ? (
            <span
              className="rounded-full px-2.5 py-0.5 text-xs font-semibold text-white"
              style={{ backgroundColor: state.draft.color }}
            >
              {state.draft.name.trim()}
            </span>
          ) : null}
        </h2>
        {group && group.usedBy.length > 0 ? (
          <span className="rounded-full border border-warning/40 bg-warning/10 px-2.5 py-1 text-xs font-medium text-warning">
            re-pools {group.usedBy.length} containers on save
          </span>
        ) : null}
      </div>

      <EditorFields
        name={state.draft.name}
        color={state.draft.color}
        countryName={countryName}
        onName={(name) => state.setDraft({ ...state.draft, name })}
        onColor={(color) => state.setDraft({ ...state.draft, color })}
      />

      <section className="flex flex-col gap-2">
        <div className="flex items-center justify-between border-b border-border/60 pb-2">
          <h3 className="flex items-center gap-2 text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">
            <Rows3 className="h-3.5 w-3.5" /> Membership rule
          </h3>
          <Link
            to="/admin/tag-rules?tab=static"
            className="text-xs font-medium text-primary hover:underline"
          >
            Manage list tags &amp; name rules →
          </Link>
        </div>
        <RuleBuilder
          countryKey={countryKey}
          rule={state.draft.rule}
          staticTags={staticTags.data ?? []}
          match={{
            count: resolve.data?.count ?? null,
            loading: resolve.isFetching,
          }}
          onChange={(rule) => state.setDraft({ ...state.draft, rule })}
        />
      </section>

      <MatchesRightNow
        resolve={resolve.data}
        loading={resolve.isFetching && !resolve.data}
        failed={resolve.isError}
        warming={statsWarming}
        conditionCount={state.draft.rule.nodes.length}
      />

      <div className="flex items-center gap-3 border-t border-border/60 pt-4">
        <Button disabled={!state.valid || state.busy} onClick={state.save}>
          Save group
        </Button>
        <Button variant="ghost" onClick={() => onDone()}>
          Cancel
        </Button>
        <span className="grow" />
        {!isNew && group ? (
          <Button
            variant="destructive"
            disabled={state.busy}
            onClick={() => state.setConfirmingDelete(true)}
          >
            Delete group
          </Button>
        ) : null}
      </div>

      <ConfirmDialog
        open={state.confirmingDelete}
        title="Delete this group?"
        description={`"${group?.name ?? ''}" will be removed. Nothing else changes — lists and their tags stay as they are.`}
        confirmLabel="Delete group"
        destructive
        busy={state.busy}
        onConfirm={state.removeGroup}
        onCancel={() => state.setConfirmingDelete(false)}
      />
    </Card>
  );
};
